import { useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useLanguage } from '@/contexts/LanguageContext';

interface HealthBenefitModalProps {
  milestoneDays: number;
  isUnlocked: boolean;
  onClose: () => void;
  onShowReward?: () => void; // Only provided when unlocked
}

// Get display text for milestone (100 → 💯)
const getMilestoneDisplay = (days: number): string => {
  return days === 100 ? '💯' : String(days);
};

export function HealthBenefitModal({ milestoneDays, isUnlocked, onClose, onShowReward }: HealthBenefitModalProps) {
  const { t } = useLanguage();

  // Lock body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Get translation keys for this milestone
  const physicalKey = `healthBenefit${milestoneDays}Physical`;
  const mentalKey = `healthBenefit${milestoneDays}Mental`;
  const sourceKey = `healthBenefit${milestoneDays}Source`;
  const sourceLinkKey = `healthBenefit${milestoneDays}SourceLink`;

  const physicalText = t(physicalKey);
  const mentalText = t(mentalKey);
  const sourceText = t(sourceKey);
  const sourceLink = t(sourceLinkKey);

  // Check if source is available (not returning the key itself)
  const hasSource = sourceText !== sourceKey && sourceText.length > 0;
  const hasSourceLink = sourceLink !== sourceLinkKey && sourceLink.length > 0;

  return (
    <div 
      className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm flex flex-col items-center p-6 overflow-y-auto overscroll-contain" 
      style={{ maxHeight: '100dvh' }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
        aria-label="Close"
      >
        <X className="h-8 w-8 text-foreground" />
      </button>
      
      <div className="w-full max-w-sm flex flex-col items-center space-y-3 my-auto py-4">
        {/* Milestone numeric visual - 4:3 aspect ratio */}
        <div className="w-full">
          <AspectRatio ratio={4/3} className="bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
            <div className="flex flex-col items-center justify-center h-full w-full">
              <span className="text-primary font-bold leading-none" style={{ fontSize: 'min(20vw, 80px)' }}>
                {getMilestoneDisplay(milestoneDays)}
              </span>
              <span className="text-primary/60 font-medium leading-none" style={{ fontSize: 'min(10vw, 40px)' }}>
                /365
              </span>
            </div>
          </AspectRatio>
        </div>
        
        {/* Health benefit content - unified list */}
        <div className="w-full text-foreground">
          <p className="text-base leading-relaxed whitespace-pre-line">
            {physicalText}
            {'\n'}
            {mentalText}
          </p>
        </div>
        
        {/* Source reference */}
        {hasSource && (
          <div className="w-full pt-2">
            {hasSourceLink ? (
              <a 
                href={sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground/70 hover:text-muted-foreground flex items-center gap-1"
              >
                {sourceText}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <p className="text-xs text-muted-foreground/70">
                {sourceText}
              </p>
            )}
          </div>
        )}
        
        {/* CTA - depends on unlock state */}
        <Button 
          onClick={isUnlocked && onShowReward ? onShowReward : onClose}
          className="w-full h-14 text-lg"
          size="lg"
        >
          {isUnlocked ? t('healthBenefitShowReward') : t('healthBenefitClose')}
        </Button>
      </div>
    </div>
  );
}
